import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ArtworkGallery from '../components/ArtworkGallery';
import { ALL_TAG } from '../data/gallery';
import { useGalleryItem } from '../hooks/useGalleryItem';
import { useLanguage } from '../contexts/LanguageContext';
import { translate } from '../i18n';

const ArtworkDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { locale } = useLanguage();

  const { item, loading, error, notFound } = useGalleryItem(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!slug || notFound) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return <p className="empty-state">{translate(locale, 'loadingState')}</p>;
  }

  if (error || !item) {
    return <p className="empty-state">{translate(locale, 'errorState')}</p>;
  }

  const galleryLabels = {
    next: translate(locale, 'nextImage'),
    previous: translate(locale, 'previousImage')
  };

  const tags = item.tags.length ? item.tags : [];
  const authorName = item.author ? item.author.name[locale] || item.author.name.en : '';

  return (
    <article className="artwork-detail" aria-labelledby="artwork-title">
      <Link className="back-link" to="/">
        ← {translate(locale, 'closeDetails')}
      </Link>

      <div className="artwork-detail__grid">
        <ArtworkGallery media={item.media} title={item.title[locale]} labels={galleryLabels} />
        <section className="artwork-detail__meta">
          <header>
            <h1 id="artwork-title">{item.title[locale]}</h1>
            <p className="artwork-detail__year">
              {translate(locale, 'yearLabel')}: {item.year}
            </p>
            {item.author ? (
              <p className="artwork-detail__author">
                {translate(locale, 'authorLabel')}:&nbsp;
                <Link to={`/authors?authorId=${item.author.id}`}>{authorName}</Link>
              </p>
            ) : null}
          </header>
          <p className="artwork-detail__description">{item.description[locale]}</p>
          <ul className="artwork-detail__tags" aria-label={translate(locale, 'filterLabel')}>
            {tags.length > 0
              ? tags.map((tag) => <li key={tag.id}>{tag.label[locale]}</li>)
              : [
                  <li key={ALL_TAG}>{translate(locale, 'allTag')}</li>
                ]}
          </ul>
        </section>
      </div>
    </article>
  );
};

export default ArtworkDetailPage;
